import {Segment} from 'platform/components';
import {Box, Heading, VStack, Show} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {EmissionsGroup} from '../../kit/groups/EmissionsGroup';
import {FuelConsumptionGroup} from '../../kit/groups/FuelConsumptionGroup';
import {WltpBasicConsumptionGroup} from '../../kit/groups/WltpBasicConsumptionGroup';
import {WltpElectricConsumptionGroup} from '../../kit/groups/WltpElectricConsumptionGroup';
import {WltpPluginHybridConsumptionGroup} from '../../kit/groups/WltpPluginHybridConsumptionGroup';
import {useGetTenantInfo} from '../../kit/hooks/useGetTenantInfo';
import {useVehicleFormContext} from '../../kit/hooks/useVehicleFormContext';
import {isTrailerOrSemitrailer} from '../../kit/utils/isTrailerOrSemitrailer';
import {VehicleForm as VFKit} from '../../kit/VehicleForm';

const CONSUMPTION_STANDARD_WLTP = 'WLTP';
const CONSUMPTION_STANDARD_NEDC = 'NEDC';

type ConsumptionStandard = typeof CONSUMPTION_STANDARD_WLTP | typeof CONSUMPTION_STANDARD_NEDC;

export function ConsumptionEmissionsSubSection() {
  const VOLUME_METRIC_SUFFIX = i18n.t('general.metric.l100km');
  const MASS_METRIC_SUFFIX = i18n.t('general.metric.kg100km');
  const ELECTRIC_METRIC_SUFFIX = i18n.t('general.metric.kwh100km');

  const {isGermanTenant} = useGetTenantInfo();
  const {formApi} = useVehicleFormContext();

  // Initialize segment based on existing consumption data
  const [consumptionStandard, setConsumptionStandard] = useState<ConsumptionStandard>(() => {
    const values = formApi.getValues();

    // Check if any WLTP data exists (liquid + electric)
    const hasWltpData = [
      values.fuelConsumptionWltpCombined,
      values.fuelConsumptionWltpCityCenter,
      values.fuelConsumptionWltpOutskirts,
      values.fuelConsumptionWltpCountryRoads,
      values.fuelConsumptionWltpHighway,
      values.fuelConsumptionWltpWeighted,
      values.fuelConsumptionWltpCombinedKwh,
      values.fuelConsumptionWltpCityCenterKwh,
      values.fuelConsumptionWltpOutskirtsKwh,
      values.fuelConsumptionWltpCountryRoadsKwh,
      values.fuelConsumptionWltpHighwayKwh,
      values.fuelConsumptionWltpWeightedCombinedKwh,
    ].some(isNotNil);

    // Check if any NEDC data exists
    const hasNedcData = [
      values.fuelConsumptionCombined,
      values.fuelConsumptionUrban,
      values.fuelConsumptionExtraUrban,
    ].some(isNotNil);

    // Default to NEDC if only NEDC data exists, otherwise WLTP
    return hasNedcData && !hasWltpData ? CONSUMPTION_STANDARD_NEDC : CONSUMPTION_STANDARD_WLTP;
  });

  const segmentOptions: Array<{value: ConsumptionStandard; label: string}> = [
    {
      value: CONSUMPTION_STANDARD_WLTP,
      label: i18n.t('entity.vehicle.labels.consumptionStandardWltp'),
    },
    {
      value: CONSUMPTION_STANDARD_NEDC,
      label: i18n.t('entity.vehicle.labels.consumptionStandardNedc'),
    },
  ];

  return (
    <VFKit.SubSection data-testid="consumption-emissions">
      <VFKit.ShowWhen
        name="type"
        condition={(type) => !isTrailerOrSemitrailer(type?.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR')}
      >
        <VStack spacing={4}>
          <Heading size={4}>
            {i18n.t('entity.vehicle.labels.consumptionAndEmissionSectionTitle')}
          </Heading>

          <Box width="fit-content">
            <Segment<ConsumptionStandard>
              value={consumptionStandard}
              onChange={(value) => {
                setConsumptionStandard(value);
              }}
              options={segmentOptions}
            />
          </Box>

          <Show when={consumptionStandard === CONSUMPTION_STANDARD_WLTP}>
            <>
              {/* Plugin Hybrid layout (HYBRIDTYPE_PHEV) */}
              <VFKit.ShowWhen
                watchFields={['fuelType', 'hybridType']}
                condition={(values) =>
                  values.fuelType?.[0] === 'FUELTYPE_HYBRID' &&
                  values.hybridType?.[0] === 'HYBRIDTYPE_PHEV'
                }
              >
                <WltpPluginHybridConsumptionGroup fuelSuffix={VOLUME_METRIC_SUFFIX} />
                <VFKit.Group data-testid="emissions" columns={[1, 2, 4]}>
                  <EmissionsGroup />
                  <Show when={isGermanTenant}>
                    <VFKit.Suspense>
                      <VFKit.UmweltplaketteSelect />
                    </VFKit.Suspense>
                  </Show>
                </VFKit.Group>
              </VFKit.ShowWhen>

              {/* Basic layout (all other hybrids and non-hybrids) */}
              <VFKit.ShowWhen
                watchFields={['fuelType', 'hybridType']}
                condition={(values) =>
                  values.fuelType?.[0] !== 'FUELTYPE_HYBRID' ||
                  values.hybridType?.[0] !== 'HYBRIDTYPE_PHEV'
                }
              >
                {/* Electric - kWh/100km */}
                <VFKit.ShowWhen
                  name="fuelType"
                  condition={(fuelType) => fuelType?.[0] === 'FUELTYPE_ELECTRIC'}
                >
                  <VFKit.Group data-testid="emissions" columns={[1, 2, 4]}>
                    <WltpElectricConsumptionGroup />
                    <EmissionsGroup />
                    <Show when={isGermanTenant}>
                      <VFKit.Suspense>
                        <VFKit.UmweltplaketteSelect />
                      </VFKit.Suspense>
                    </Show>
                  </VFKit.Group>
                </VFKit.ShowWhen>

                {/* Ethanol/Hydrogen - kg/100km */}
                <VFKit.ShowWhen
                  name="fuelType"
                  condition={(fuelType) => {
                    const primaryFuel = fuelType?.[0];
                    return (
                      primaryFuel === 'FUELTYPE_ETHANOL' || primaryFuel === 'FUELTYPE_HYDROGEN'
                    );
                  }}
                >
                  <VFKit.Group data-testid="emissions" columns={[1, 2, 4]}>
                    <WltpBasicConsumptionGroup suffix={MASS_METRIC_SUFFIX} />
                    <EmissionsGroup />
                    <Show when={isGermanTenant}>
                      <VFKit.Suspense>
                        <VFKit.UmweltplaketteSelect />
                      </VFKit.Suspense>
                    </Show>
                  </VFKit.Group>
                </VFKit.ShowWhen>

                {/* All other fuels - l/100km */}
                <VFKit.ShowWhen
                  name="fuelType"
                  condition={(fuelType) => {
                    const primaryFuel = fuelType?.[0];
                    return (
                      primaryFuel !== 'FUELTYPE_ELECTRIC' &&
                      primaryFuel !== 'FUELTYPE_ETHANOL' &&
                      primaryFuel !== 'FUELTYPE_HYDROGEN'
                    );
                  }}
                >
                  <VFKit.Group data-testid="emissions" columns={[1, 2, 4]}>
                    <WltpBasicConsumptionGroup suffix={VOLUME_METRIC_SUFFIX} />
                    <EmissionsGroup />
                    <Show when={isGermanTenant}>
                      <VFKit.Suspense>
                        <VFKit.UmweltplaketteSelect />
                      </VFKit.Suspense>
                    </Show>
                  </VFKit.Group>
                </VFKit.ShowWhen>
              </VFKit.ShowWhen>
            </>
          </Show>

          <Show when={consumptionStandard === CONSUMPTION_STANDARD_NEDC}>
            <VFKit.Group data-testid="emissions" columns={[1, 2, 4]}>
              <VFKit.ShowWhen name="fuelType" value={['FUELTYPE_ELECTRIC']}>
                <FuelConsumptionGroup fuelSuffix={ELECTRIC_METRIC_SUFFIX} />
              </VFKit.ShowWhen>
              <VFKit.ShowWhen
                name="fuelType"
                condition={(fuelType) => {
                  const primaryFuel = fuelType?.[0];
                  return primaryFuel === 'FUELTYPE_ETHANOL' || primaryFuel === 'FUELTYPE_HYDROGEN';
                }}
              >
                <FuelConsumptionGroup fuelSuffix={MASS_METRIC_SUFFIX} />
              </VFKit.ShowWhen>
              <VFKit.ShowWhen
                name="fuelType"
                condition={(fuelType) => {
                  const primaryFuel = fuelType?.[0];
                  return (
                    primaryFuel !== 'FUELTYPE_ELECTRIC' &&
                    primaryFuel !== 'FUELTYPE_ETHANOL' &&
                    primaryFuel !== 'FUELTYPE_HYDROGEN'
                  );
                }}
              >
                <FuelConsumptionGroup />
              </VFKit.ShowWhen>
              {/* Keep the old layout */}
              <div />
              <EmissionsGroup />
              <Show when={isGermanTenant}>
                <VFKit.Suspense>
                  <VFKit.UmweltplaketteSelect />
                </VFKit.Suspense>
              </Show>
            </VFKit.Group>
          </Show>

          <VFKit.ParticleFilterCheckbox />
        </VStack>
      </VFKit.ShowWhen>
    </VFKit.SubSection>
  );
}
